import React, { useEffect, useState, useCallback, useRef } from "react";
import { Transition } from "history";
import { useBlocker } from "./blocker";
import { Modal } from "../shared-components/Modal";
import Button, { ButtonColorTypes } from "../shared-components/Button";

interface BlockerRegistration {
  id: string;
  title: string;
  message: string;
  onLeave: () => void;
}

interface PreventLeaveContextValue {
  register: (registration: BlockerRegistration) => void;
  unregister: (id: string) => void;
  isBlocking: boolean;
}

const PreventLeaveContext =
  React.createContext<PreventLeaveContextValue | null>(null);

let idCounter = 0;
function generateId(): string {
  return `prevent-leave-${++idCounter}`;
}

interface UsePreventLeaveOptions {
  when: boolean;
  title?: string;
  message?: string;
  onLeave?: () => void;
}

interface UsePreventLeaveReturn {
  LeaveWarningModal: React.ReactElement | null;
  isWarningModalOpen: boolean;
}

export function usePreventLeave({
  when,
  title = "Unsaved Changes",
  message = "You have unsaved changes. If you leave this page, your changes will be lost.",
  onLeave,
}: UsePreventLeaveOptions): UsePreventLeaveReturn {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = generateId();
  }
  const id = idRef.current;

  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const context = React.useContext(PreventLeaveContext);
  const hasContext = context !== null;

  const registerRef = useRef(context?.register);
  const unregisterRef = useRef(context?.unregister);
  registerRef.current = context?.register;
  unregisterRef.current = context?.unregister;

  const [showLeaveWarningModal, setShowLeaveWarningModal] = useState(false);
  const [pendingTransition, setPendingTransition] = useState<Transition | null>(
    null
  );

  useEffect(() => {
    if (!hasContext) return;
    const register = registerRef.current;
    const unregister = unregisterRef.current;
    if (!register || !unregister) return;

    if (when) {
      register({
        id,
        title,
        message,
        onLeave: () => onLeaveRef.current?.(),
      });
    } else {
      unregister(id);
    }

    return () => {
      unregister(id);
    };
  }, [when, id, title, message, hasContext]);

  useEffect(() => {
    if (hasContext) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (when) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    if (when) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when, hasContext]);

  useEffect(() => {
    if (hasContext) return;

    if (!when) {
      setPendingTransition(null);
      setShowLeaveWarningModal(false);
    }
  }, [when, hasContext]);

  const shouldBlock = !hasContext && when;
  useBlocker((tx: Transition) => {
    if (when) {
      setPendingTransition(tx);
      setShowLeaveWarningModal(true);
    } else {
      tx.retry();
    }
  }, shouldBlock);

  const handleLeavePage = useCallback(() => {
    const transitionToRetry = pendingTransition;
    setShowLeaveWarningModal(false);
    setPendingTransition(null);

    onLeaveRef.current?.();

    if (transitionToRetry) {
      setTimeout(() => {
        transitionToRetry.retry();
      }, 0);
    }
  }, [pendingTransition]);

  const handleStayOnPage = useCallback(() => {
    setShowLeaveWarningModal(false);
    setPendingTransition(null);
  }, []);

  if (hasContext) {
    return {
      LeaveWarningModal: null,
      isWarningModalOpen: false,
    };
  }

  const LeaveWarningModal = (
    <Modal
      open={showLeaveWarningModal}
      onClose={handleStayOnPage}
      title={title}
      width="w-full max-w-md"
    >
      <div className="py-4">
        <p className="text-sm text-gray-700 mb-4">{message}</p>
        <div className="flex justify-end gap-3">
          <Button
            text="Stay on Page"
            colorType={ButtonColorTypes.cancel}
            onClick={handleStayOnPage}
          />
          <Button
            text="Leave Page"
            colorType={ButtonColorTypes.secondary}
            onClick={handleLeavePage}
          />
        </div>
      </div>
    </Modal>
  );

  return {
    LeaveWarningModal,
    isWarningModalOpen: showLeaveWarningModal,
  };
}

export { PreventLeaveContext };
export type { PreventLeaveContextValue, BlockerRegistration };
